import "./ErrorPage.scss";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, isRouteErrorResponse, useRouteError, useSearchParams } from "react-router-dom";

interface ErrorPageProps {
  status?: number;
  title?: string;
  message?: string;
}

function ErrorPage({ status: statusProp, title: titleProp, message: messageProp }: ErrorPageProps) {
  const routeError = useRouteError();
  const [searchParams] = useSearchParams();

  const statusFromQueryRaw = searchParams.get("status");
  const statusFromQuery =
    statusFromQueryRaw && /^\d+$/.test(statusFromQueryRaw) ? Number(statusFromQueryRaw) : undefined;

  const statusFromRouteError = isRouteErrorResponse(routeError) ? routeError.status : undefined;

  const status = statusProp ?? statusFromQuery ?? statusFromRouteError ?? 500;

  const defaultContent = (() => {
    switch (status) {
      case 403:
        return {
          title: "Access Forbidden",
          message: "You don't have permission to access this page.",
          ctaText: "Return to Safety",
          ctaTo: "/main",
        };
      case 404:
        return {
          title: "Page Not Found",
          message:
            "The page you're looking for may have been moved, secured, or intercepted by our system.",
          ctaText: "Return to Safety",
          ctaTo: "/main",
        };
      default:
        return {
          title: "Something Went Wrong",
          message: "An unexpected error occurred. Please try again or return to a safe zone.",
          ctaText: "Return to Safety",
          ctaTo: "/main",
        };
    }
  })();

  const title = titleProp ?? defaultContent.title;
  const message =
    messageProp ??
    (status >= 500 && routeError instanceof Error && routeError.message
      ? routeError.message
      : defaultContent.message);

  return (
    <div className="error-container">
      <div className="error-message">
        <div className="error-section">
          <div className="number-container">
            <h1>{status}</h1>
          </div>
          <h2>{title}</h2>
          <p>{message}</p>
          <p>Please check the URL or navigate back to a safe zone.</p>
          <Link to={defaultContent.ctaTo} className="return-btn">
            <FontAwesomeIcon icon={faArrowLeft} />
            {defaultContent.ctaText}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ErrorPage;
